import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { extractApiErrorMessage } from "@/lib/api";
import PageShell from "@/components/PageShell";
import DataState from "@/components/DataState";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, BookOpen } from "lucide-react";
import PolicyConsentCheckbox from "@/components/PolicyConsentCheckbox";
import EmailNoticeBanner from "@/components/EmailNoticeBanner";
import PromoCodeInput from "@/components/PromoCodeInput";
import useCouponVisibility from "@/hooks/useCouponVisibility";
import useAutoDiscounts, { discountForService } from "@/hooks/useAutoDiscounts";

const BOOK_COVER =
  "https://customer-assets.emergentagent.com/job_counsel-marketplace/artifacts/jkrfq5vv_gemini-2.5-flash-image_Create_an_ultra-realistic_high-end_product_photography_image_for_an_Arabic_digit-0.jpg";

const BookPage = () => {
  const { book: showBookCoupon } = useCouponVisibility();
  const autoDiscounts = useAutoDiscounts();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [policiesAccepted, setPoliciesAccepted] = useState(false);
  const [promo, setPromo] = useState(null); // {code, discount, finalAmount}
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    api
      .get("/services/book-thabiti")
      .then((r) => setBook(r.data))
      .catch((e) => setError(extractApiErrorMessage(e, "تعذّر تحميل بيانات الكتاب")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleBuy = async () => {
    if (!user) {
      toast("يلزم تسجيل الدخول أولًا");
      navigate("/login?next=/book");
      return;
    }
    if (!policiesAccepted) {
      toast.error("يرجى قراءة سياسة الخصوصية والاسترجاع والموافقة عليها قبل المتابعة");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/create", {
        service_id: "book-thabiti",
        origin_url: window.location.origin,
        policies_accepted: true,
        ...(promo?.code ? { promo_code: promo.code } : {}),
      });
      // Iteration 24: a 100%-discount coupon bypasses Tap entirely; the API
      // returns a coupon-bypass success URL that's an absolute path on our
      // own origin.
      if (data.provider === "coupon_bypass" && data.url) {
        window.location.href = data.url;
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر بدء الدفع"));
      setSubmitting(false);
    }
  };

  if (loading || error || !book) {
    return (
      <PageShell>
        <section className="max-w-3xl mx-auto px-6 py-16">
          <DataState
            loading={loading}
            error={error}
            onRetry={load}
            empty={!loading && !book}
            emptyText="لا تتوفر بيانات الكتاب حاليًا"
            loadingText="جاري تحميل الكتاب..."
            testId="book-state"
          />
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <img
            src={BOOK_COVER}
            alt={book.title_ar}
            className="rounded-3xl border border-[#E5E1D8] w-full object-cover h-[520px]"
          />
        </div>
        <div className="lg:pt-6">
          <span className="inline-flex items-center gap-2 text-[#0A4D3C] tracking-widest text-sm">
            <BookOpen className="w-4 h-4" /> كتاب رقمي PDF
          </span>
          <h1
            data-testid="book-title"
            className="font-heading text-4xl sm:text-5xl text-[#222222] mt-3 leading-tight"
          >
            {book.title_ar}
          </h1>
          <p className="text-[#5E5C58] mt-5 leading-loose text-lg">
            {book.description_ar}
          </p>

          <ul className="mt-6 space-y-3 text-[#222222]">
            {[
              "خطة علاجية بطرق إبداعية مبتكرة وميسرة",
              "حلول للمشكلات وبرمجة العلاقات",
              "تحميل فوري بعد إتمام الدفع",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#2E7D32] shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <div
            data-testid="book-policy"
            className="mt-5 text-sm font-medium text-[#B33939] bg-rose-50 border border-rose-200 rounded-xl px-4 py-2 inline-block"
          >
            {book.policy_ar || "لا يُرد ولا يُستبدل."}
          </div>

          <div className="mt-8 flex items-baseline gap-3">
            {(() => {
              const auto = discountForService(
                { id: "book-thabiti", type: "book", price: Number(book.price) },
                autoDiscounts
              );
              if (promo) {
                return (
                  <>
                    <span className="font-heading text-3xl text-[#A8A29E] line-through" data-testid="book-price-original">
                      {Number(book.price).toLocaleString("ar-SA")}
                    </span>
                    <span className="font-heading text-5xl text-[#0A4D3C]" data-testid="book-price-final">
                      {Number(promo.finalAmount).toLocaleString("ar-SA")}
                    </span>
                  </>
                );
              }
              if (auto) {
                return (
                  <>
                    <span className="font-heading text-3xl text-[#A8A29E] line-through" data-testid="book-price-original">
                      {Number(auto.base).toLocaleString("ar-SA")}
                    </span>
                    <span className="font-heading text-5xl text-[#0A4D3C]" data-testid="book-price-final">
                      {Number(auto.final).toLocaleString("ar-SA")}
                    </span>
                    <span
                      className="ms-3 inline-block rounded-full bg-[#0A4D3C]/10 text-[#0A4D3C] text-xs px-3 py-1"
                      data-testid="book-auto-discount-badge"
                    >
                      خصم {auto.percentage}%
                    </span>
                  </>
                );
              }
              return (
                <span className="font-heading text-5xl text-[#0A4D3C]" data-testid="book-price-final">
                  {Number(book.price).toLocaleString("ar-SA")}
                </span>
              );
            })()}
            <span className="text-[#5E5C58]">ر.س</span>
          </div>

          {showBookCoupon && (
            <div className="mt-6">
              <PromoCodeInput
                serviceId="book-thabiti"
                originalAmount={Number(book.price)}
                onApplied={setPromo}
                onCleared={() => setPromo(null)}
              />
            </div>
          )}

          <PolicyConsentCheckbox
            checked={policiesAccepted}
            onChange={setPoliciesAccepted}
            className="mt-6"
            variant="book"
          />

          <EmailNoticeBanner className="mt-4" />

          <Button
            data-testid="book-buy-btn"
            onClick={handleBuy}
            disabled={submitting || !policiesAccepted}
            className="mt-4 rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white h-12 px-8 text-base"
          >
            {submitting
              ? "جاري التحويل..."
              : promo && promo.finalAmount === 0
              ? "تأكيد الطلب المجاني"
              : "اشترِ الآن"}
          </Button>
        </div>
      </section>
    </PageShell>
  );
};

export default BookPage;
